import React, { useState, useEffect } from 'react';
import { useGame } from '../context/GameContext';
import LogoScreen from '../components/LogoScreen';
import TeamSelection from '../components/TeamSelection';
import GameInterface from '../components/GameInterface';
import FinalGameInterface from '../components/FinalGameInterface';
import FinalGameResults from '../components/FinalGameResults';
import './TabletPage.css';

function TabletPage({ section }) {
  const {
    isConnected,
    currentSession,
    selectedTeam,
    registerDevice,
    selectTeam,
    socket,
    setSelectedTeam
  } = useGame();

  const [screenState, setScreenState] = useState('logo'); // logo, team-selection, game, final-game, final-results, loading
  const [assignedTeam, setAssignedTeam] = useState(null);

  useEffect(() => {
    if (isConnected) {
      registerDevice('tablet', section);
    }
  }, [isConnected, section]);

  // Listen for game end and clear team assignment
  useEffect(() => {
    if (!socket) return;

    const handleGameEnded = (data) => {
      console.log('🛑 Game ended, clearing team assignment');
      // Clear team assignment when game ends
      if (data.session?.status === 'ended' && !data.session?.finalGameActive) {
        // Don't clear immediately - wait for next game to reassign
        // This allows the loading screen to show the correct team
      }
    };

    const handleGameStarted = (data) => {
      console.log('🎮 New game started - teams reset to initial positions');
      console.log('   Initial: Alpha->Wine Cellar, Bravo->Study, Charlie->Dining, Delta->Fuse');

      // Clear old team assignment so new game can reassign based on new teamRoomAssignments
      // Tabs stay at their URLs (wine-cellar, study, dining-room, fuse-box)
      // But they will now show the teams assigned to those rooms in the new game (initial positions)
      setAssignedTeam(null);
      setSelectedTeam(null);

      // Team identification will happen automatically via the other useEffect
      // when it sees the new session with teamRoomAssignments
    };

    const handleFinalGameStarted = (data) => {
      console.log('🏆 Final game started, restoring team assignment');
      // Restore selectedTeam from assignedTeam when final game starts
      // This ensures the team is available for final game answer submissions
      if (assignedTeam && !selectedTeam) {
        console.log('✅ Restoring selectedTeam from assignedTeam:', assignedTeam.name);
        setSelectedTeam(assignedTeam);
      }
    };

    socket.on('game:ended', handleGameEnded);
    socket.on('game:started', handleGameStarted);
    socket.on('final-game:started', handleFinalGameStarted);

    return () => {
      socket.off('game:ended', handleGameEnded);
      socket.off('game:started', handleGameStarted);
      socket.off('final-game:started', handleFinalGameStarted);
    };
  }, [socket, setSelectedTeam, assignedTeam, selectedTeam]);

  // Listen for round changes - NO REDIRECT, just update team assignment
  // Tablets are now fixed to rooms, teams rotate
  useEffect(() => {
    if (!socket) return;

    const handleRoundNext = (data) => {
      console.log('🔄 Round changed - teams rotated, tablets stay in place', data);
      // The team assignment will be automatically updated by the auto-identification useEffect
      // No redirect needed - tablets stay at their fixed room URLs
    };

    socket.on('round:next', handleRoundNext);

    return () => {
      socket.off('round:next', handleRoundNext);
    };
  }, [socket]);

  // Auto-identify team based on room assignment when game starts or after redirect
  useEffect(() => {
    if (!currentSession || !isConnected) return;

    console.log('🔍 Checking team assignment for section:', section);
    console.log('🔍 Current session round:', currentSession.currentRound);
    console.log('🔍 Team room assignments:', currentSession.teamRoomAssignments);

    // For new round-based system, identify team from room assignment
    if (currentSession.teamRoomAssignments && currentSession.teamRoomAssignments.length > 0) {
      const assignment = currentSession.teamRoomAssignments.find(
        a => a.currentRoom === section
      );

      if (assignment) {
        // Check if team changed (important for redirects)
        const newTeamId = assignment.teamId.toString();
        const currentTeamId = assignedTeam?.id?.toString();

        if (newTeamId !== currentTeamId) {
          console.log('🎯 Auto-assigning team:', assignment.teamName, 'to room:', section, '(Round', currentSession.currentRound + ')');
          const team = {
            id: newTeamId, // Store as string for consistency
            name: assignment.teamName
          };
          setAssignedTeam(team);
          // Also set in context so other components can access it
          setSelectedTeam(team);
        } else {
          console.log('✅ Team already assigned:', assignedTeam?.name);
        }
      } else {
        console.log('⚠️ No assignment found for section:', section);
      }
    }
  }, [currentSession, isConnected, section, assignedTeam, setSelectedTeam]);

  // Ensure selectedTeam is set when final game is active
  useEffect(() => {
    if (currentSession?.status === 'final-game' && currentSession?.finalGameActive) {
      // If final game is active but selectedTeam is not set, restore it from assignedTeam
      if (assignedTeam && !selectedTeam) {
        console.log('🏆 Final game active - restoring selectedTeam from assignedTeam:', assignedTeam.name);
        setSelectedTeam(assignedTeam);
      }
    }
  }, [currentSession?.status, currentSession?.finalGameActive, assignedTeam, selectedTeam, setSelectedTeam]);

  useEffect(() => {
    // Check if we're in loading state (after continue button clicked)
    const isInLoading = localStorage.getItem('finalGameLoading') === 'true';
    if (isInLoading && currentSession?.status === 'ended') {
      setScreenState('loading');
      return;
    }

    if (!currentSession) {
      setScreenState('logo');
      // Clear loading state and team if no session
      if (isInLoading) {
        localStorage.removeItem('finalGameLoading');
      }
      setAssignedTeam(null);
      return;
    }

    // When timer ends (status = 'ended' and not final game), show loading screen
    if (currentSession.status === 'ended' && !currentSession.finalGameActive) {
      setScreenState('loading');
      return;
    }

    // Check if final game has ended and show results (only if not already showing loading)
    if (currentSession.status === 'ended' && !currentSession.finalGameActive && (selectedTeam || assignedTeam) && !isInLoading) {
      // This case is now handled above, but keeping for safety
      setScreenState('loading');
      return;
    }

    if (currentSession.tabletsLocked && currentSession.status !== 'ended') {
      setScreenState('logo');
      return;
    }

    // Final game: Show to all tablets when active
    if (currentSession.status === 'final-game' && currentSession.finalGameActive) {
      // Use either selectedTeam or assignedTeam
      const team = selectedTeam || assignedTeam;
      if (!team) {
        setScreenState('team-selection');
        return;
      }
      setScreenState('final-game');
      return;
    }

    // For round-based system with pre-assigned teams (Rounds 1-4)
    // Teams are automatically assigned based on teamRoomAssignments - NO team selection screen
    if (currentSession.currentRound > 0 && currentSession.currentRound <= 4 && currentSession.teamRoomAssignments) {
      const team = selectedTeam || assignedTeam;

      // If no team assigned yet, wait for auto-assignment (show logo/loading)
      if (!team) {
        console.log('⏳ Waiting for team auto-assignment...');
        setScreenState('logo');
        return;
      }

      // No team selection needed - teams are pre-assigned
      if (currentSession.status === 'waiting' && team) {
        setScreenState('waiting');
        return;
      }

      if (currentSession.status === 'active' && team) {
        setScreenState('game');
        return;
      }
    }

    // Legacy team selection flow - only for old sessions without teamRoomAssignments
    // This should not happen in new flow, but keeping for backward compatibility
    if (currentSession.status === 'team-selection' && !currentSession.teamRoomAssignments && !selectedTeam && !assignedTeam) {
      setScreenState('team-selection');
      return;
    }

    if (currentSession.status === 'active' && (selectedTeam || assignedTeam)) {
      setScreenState('game');
      return;
    }

    if ((selectedTeam || assignedTeam) && currentSession.status === 'team-selection') {
      setScreenState('waiting');
      return;
    }

  }, [currentSession, selectedTeam, assignedTeam]);

  const handleTeamSelect = async (teamId) => {
    const success = await selectTeam(teamId);
    if (success) {
      setScreenState('waiting');
    }
  };

  const getSectionTitle = () => {
    switch (section) {
      case 'wine-cellar':
        return 'The Wine Cellar';
      case 'study':
        return 'The Study';
      case 'dining-room':
        return 'The Dining Room';
      case 'fuse-box':
        return 'The Fuse Box';
      default:
        return 'Game Section';
    }
  };

  // Logo screen (waiting for game to start)
  if (screenState === 'logo') {
    return <LogoScreen />;
  }

  // Team selection screen
  if (screenState === 'team-selection') {
    return (
      <TeamSelection
        section={section}
        sectionTitle={getSectionTitle()}
        onTeamSelect={handleTeamSelect}
      />
    );
  }

  // Waiting for game to start after team selection
  if (screenState === 'waiting') {
    const team = selectedTeam || assignedTeam;
    const roundText = currentSession?.currentRound > 0 ? ` - Round ${currentSession.currentRound}` : '';

    return (
      <div className="tablet-container waiting-screen">
        <div className="waiting-content">
          <div className="waiting-logo-container">
            <img
              src="/fortuin.gif"
              alt="Escape Room Logo"
              className="waiting-logo-gif"
            />
          </div>
          <h1>Welcome, {team?.name}!</h1>
          <h2>{getSectionTitle()}{roundText}</h2>
          <p className="waiting-message">
            Waiting for gamemaster to start the game...
          </p>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  // Final game screen
  if (screenState === 'final-game') {
    return <FinalGameInterface section={section} />;
  }

  // Final game results screen
  if (screenState === 'final-results') {
    return <FinalGameResults />;
  }

  // Loading screen (after continue button)
  if (screenState === 'loading') {
    return (
      <div className="tablet-container loading-screen">
        <div className="loading-content">
          <div className="loading-logo-container">
            <img
              src="/fortuin.gif"
              alt="Escape Room Logo"
              className="loading-logo-gif"
            />
          </div>
          <h1>Preparing Next Game...</h1>
          <div className="spinner"></div>
          <p className="loading-message">
            Waiting for gamemaster to start a new game...
          </p>
        </div>
      </div>
    );
  }

  // Main game interface
  if (screenState === 'game') {
    const team = selectedTeam || assignedTeam;
    const roundText = currentSession?.currentRound > 0 ? ` - Round ${currentSession.currentRound}` : '';

    // If no team identified yet, show a debug screen
    if (!team) {
      return (
        <div className="tablet-container waiting-screen">
          <div className="waiting-content">
            <h1>⚠️ Team Not Identified</h1>
            <p>Refreshing...</p>
            <div className="spinner"></div>
            <pre style={{ textAlign: 'left', fontSize: '10px', marginTop: '20px' }}>
              Debug Info:
              Section: {section}
              Round: {currentSession?.currentRound}
              Has Assignments: {currentSession?.teamRoomAssignments ? 'Yes' : 'No'}
              Assignments Count: {currentSession?.teamRoomAssignments?.length || 0}
            </pre>
          </div>
        </div>
      );
    }

    return (
      <GameInterface
        section={section}
        sectionTitle={getSectionTitle() + roundText}
      />
    );
  }

  return <LogoScreen />;
}

export default TabletPage;

